<script setup lang="ts">
import { useLandingPage } from '~/composables/content/landing'

import { UiAvatar, UiHubCard } from '@ui'
import PostComponent from '@ui/composites/PostComponent/PostComponent.vue'
import AppStore from '~/components/reusables/AppStore.vue'

import ITelegram from '/assets/images/icon-telegram.svg?component'

import GooglePlay from '~/components/reusables/GooglePlay.vue'
import type { EventEntry, HubItem } from '~~/types/types'
import RelatedEvents from '~/components/ui/RelatedEvents.vue'

definePageMeta({
  alias: '/:slug',
  layout: 'default',
})

const route = useRoute()
const localeRoute = useLocaleRoute()

const { t } = useI18n()

const { pageData } = await useLandingPage()

const {
  public: { appLink },
} = useRuntimeConfig()

const slug = computed(() => route.params.slug as string)
const hubId = computed(() => pageData.value.hubId)

const title = computed(
  () => pageData.value?.seo?.metaTitle || pageData.value?.title || ''
)
const description = computed(
  () =>
    pageData.value?.seo?.metaDescription || pageData.value?.description || ''
)
const image = computed(
  () =>
    pageData.value?.seo?.metaImage?.url || pageData.value?.backgroundImage || ''
)
const consumerPixelId = computed(() => pageData.value?.analytic?.meta_pixel_id)

useSeoMeta({
  title: title,
  description: description,
  ogTitle: title,
  ogDescription: description,
  ogImage: image,
  ogImageWidth: '1200',
  ogImageHeight: '630',
  ogType: 'website',
  twitterTitle: title,
  twitterDescription: description,
  twitterImage: image,
  twitterCard: 'summary_large_image',
})

// meta pixel
useConsumerMetaPixel({
  id: consumerPixelId,
})

const isChainatlasOrTonx = computed(
  () =>
    slug.value === 'chainatlas-skale' ||
    slug.value === 'chainatlas' ||
    slug.value === 'tonxdao'
)

const hubs =
  pageData.value.slug === 'chainatlas'
    ? ['chainatlas-skale', 'tonxdao']
    : pageData.value.slug === 'chainatlas-skale'
      ? ['chainatlas', 'tonxdao']
      : pageData.value.slug === 'tonxdao'
        ? ['chainatlas', 'chainatlas-skale']
        : []

const hubApiDataArray = ref<HubItem[]>([])

if (isChainatlasOrTonx.value) {
  const results = await Promise.all(
    hubs.map(async (hub) => {
      const { data } = await useNuxtApp().$client.hubs.getHub.useQuery(hub)
      return data.value?.data as HubItem | undefined
    })
  )
  hubApiDataArray.value = results.filter(
    (item): item is HubItem => item !== undefined
  )
}

const { data: hubApiData, error } =
  await useNuxtApp().$client.hubs.getHub.useQuery(hubId.value)

const { data: relatedHubsData } =
  await useNuxtApp().$client.hubs.getRelatedHubsById.useQuery(`${slug.value}`)

// TODO: replace to use all in one query
const featuredEventData: EventEntry[] = await Promise.all(
  (pageData.value?.featured_events || []).map(async ({ event_id }) => {
    const { data } =
      await useNuxtApp().$client.hubs.getFeaturedEvent.useQuery(event_id)
    return data.value as EventEntry
  })
)

const relatedHubs = computed(() => {
  return relatedHubsData.value ?? []
})

interface Partner {
  id: number
  name: string
  src: string
  href: string
  class?: string
}

const partnerSizeClasses: { [key: number]: string } = {
  0: 'min-w-33 max-w-33',
  1: 'min-w-7.5 max-w-7.5',
  2: 'min-w-30 max-w-30',
  3: 'min-w-7.5 max-w-7.5',
  4: 'min-w-50 max-w-50',
  5: 'min-w-39 max-w-39',
  6: 'min-w-7.5 max-w-7.5',
  7: 'min-w-13.25 max-w-13.25',
  8: 'min-w-9.5 max-w-9.5',
  9: 'min-w-45.5 max-w-45.5',
}

const partnerList: Partner[] = [
  {
    id: 0,
    name: 'Starknet',
    src: 'belong:starknet-logo',
    class: 'w-33',
    href: 'https://lu.ma/o0yv6mka',
  },
  {
    id: 1,
    name: 'The sandbox sand',
    src: 'belong:the-sandbox-sand-logo',
    class: 'w-7.5',
    href: 'https://lu.ma/qxdevbl4',
  },
  {
    id: 2,
    name: 'Coin Telegraph',
    src: 'belong:cointelegraph',
    class: 'w-30',
    href: 'https://lu.ma/ykmvsxdv ',
  },
  {
    id: 3,
    name: 'No Name',
    src: 'belong:noname',
    class: 'w-7.5',
    href: 'https://lu.ma/wz5kk3nk',
  },
  {
    id: 4,
    name: 'Wow Summit',
    src: 'belong:wow-summit',
    class: 'w-50',
    href: 'https://bangkok2024.wowsummit.net/ ',
  },
  {
    id: 5,
    name: 'Bit Angels',
    src: 'belong:bit-angels',
    class: 'w-39',
    href: 'https://lu.ma/xyicy5re',
  },
  {
    id: 6,
    name: 'Singularity Dao',
    src: 'belong:singularity-dao',
    class: 'w-7.5',
    href: 'https://lu.ma/SuperintelligenceSummit ',
  },
  {
    id: 7,
    name: 'Mysten',
    src: 'belong:mysten',
    class: 'w-13.25',
    href: 'https://lu.ma/suiconnectbangkok',
  },
  {
    id: 8,
    name: 'Aethir',
    src: 'belong:aethir',
    class: 'w-9.5',
    href: 'https://lu.ma/17uwuz0h',
  },
  {
    id: 9,
    name: 'Wallet Connect',
    src: 'belong:wallet-connect-logo',
    class: 'w-45.5',
    href: 'https://lu.ma/welcometobkk',
  },
]

const swiperBreakpoints = computed(() => ({
  320: { slidesPerView: 1.1 },
  640: { slidesPerView: 1.8 },
  1024: { slidesPerView: 2.8 },
  1440: { slidesPerView: 3.5 },
  1920: { slidesPerView: 4.5 },
  2560: { slidesPerView: 5.5 },
  3840: { slidesPerView: 6.5 },
}))

const isPlayapal = computed(
  () =>
    pageData.value.slug === 'playapal' || pageData.value.slug === 'burnermap'
)
</script>

<template>
  <div v-if="pageData">
    <section>
      <div class="max-w-244 relative mt-3.5 mx-5 lg:mt-11 lg:mx-auto">
        <img
          v-if="pageData.backgroundImage?.url"
          class="h-90 object-cover w-full object-center rounded-2xl"
          alt="Banner"
          :src="pageData.backgroundImage.url"
        />

        <div
          class="w-17 h-17 rounded-lg overflow-hidden absolute left-2.5 lg:left-5 -bottom-8.5"
        >
          <video
            v-if="slug === 'alchemy-family'"
            class="w-full h-full object-contain"
            autoplay
            loop
            muted
            playsinline
          >
            <source :src="pageData.heroImage?.url" type="video/mp4" />
          </video>
          <img
            v-else-if="pageData.heroImage?.url"
            :src="pageData.heroImage.url"
            alt=""
            class="w-full h-full object-cover"
          />
        </div>
      </div>

      <div class="relative overflow-hidden z-1">
        <template v-if="slug === 'burnermap'">
          <p class="text-lg pt-14 max-w-244 mx-auto text-center px-5 lg:px-0">
            A brand new BurnerMap coming soon! Expected launch date July 10th
          </p>
        </template>
        <div
          class="flex flex-col items-center pt-11.5 pb-5 lg:flex-row lg:items-start lg:justify-start lg:gap-17.5 lg:pt-14 lg:max-w-244 lg:mx-auto"
        >
          <div class="w-full max-w-140 lg:text-left">
            <div
              class="flex flex-col gap-5 pb-6 px-5 items-center lg:items-start lg:pb-0 lg:px-0"
            >
              <h1
                class="text-[1.75rem] leading-7.5 tracking-wide lg:text-11 lg:leading-12 capitalize"
              >
                <template v-if="pageData.title.includes('Chain Atlas:')">
                  <span
                    style="
                      background:
                        linear-gradient(
                          90deg,
                          #ffb0f0 1.4%,
                          #4467ff 22.6%,
                          #7540ee 53.1%
                        ),
                        #ffffff;
                      -webkit-background-clip: text;
                      -webkit-text-fill-color: transparent;
                      background-clip: text;
                      text-fill-color: transparent;
                    "
                    >Chain Atlas:</span
                  >
                  {{ pageData.title.replace('Chain Atlas: ', '') }}
                </template>
                <template v-else>{{ pageData.title }}</template>
              </h1>
              <div>
                <div
                  class="prose prose-invert text-[#D3D3D3] tracking-wide text-base whitespace-pre-line leading-tight font-[Montserrat]"
                >
                  <V-HTML :html="pageData!.description"></V-HTML>
                </div>
                <p
                  v-if="!isChainatlasOrTonx"
                  class="text-xs text-[#808080] mt-4"
                >
                  {{ t('landing-note', { title: pageData.title }) }}
                </p>
              </div>
              <div
                class="flex flex-col max-w-full items-center gap-5 lg:items-start max-[425px]:w-full"
              >
                <NuxtLink
                  v-if="!isChainatlasOrTonx"
                  class="bg-gradient-to-r from-[#7540EE] to-[#AF55FF] rounded-full flex max-w-full px-3.5 py-2 cursor-pointer w-full sm:w-auto"
                  :to="
                    pageData.slug === 'playapal'
                      ? 'https://app.playapal.org/'
                      : pageData.slug === 'burnermap'
                        ? 'https://app.burnermap.com/'
                        : `/hubs/${hubApiData?.data.slug}`
                  "
                  target="_blank"
                >
                  <p class="truncate mx-auto">
                    {{ t('join', { name: pageData.title }) }}
                  </p>
                </NuxtLink>
                <div
                  v-if="!isChainatlasOrTonx"
                  class="flex flex-row gap-5 flex-wrap justify-center"
                >
                  <AppStore />
                  <GooglePlay />
                </div>
              </div>

              <div
                v-if="slug === 'lampu-2024'"
                class="flex flex-col flex-wrap gap-3.5 mt-7.5 items-center lg:flex-row lg:items-start"
              >
                <NuxtLink
                  class="bg-black rounded-full flex flex-row text-[13px] h-10 font-medium tracking-wide items-center cursor-pointer pl-2.5 pr-4.5"
                  to="https://t.me/lampu_bali"
                  target="_blank"
                >
                  <ITelegram class="mr-3" />
                  {{ t('join-bali-burners-chat') }}
                </NuxtLink>
                <NuxtLink
                  class="bg-black rounded-full flex flex-row text-[13px] h-10 font-medium tracking-wide items-center cursor-pointer pl-2.5 pr-4.5"
                  to="https://t.me/lampu_bali"
                  target="_blank"
                >
                  <ITelegram class="mr-3" />

                  {{ t('join-announcements-channel') }}
                </NuxtLink>
              </div>
            </div>
          </div>
          <div
            class="hidden lg:flex lg:justify-center lg:my-5 lg:mx-5 lg:mt-10 lg:m-0 lg:items-start"
            v-if="pageData.heroImage?.url"
          >
            <div class="max-w-75 max-h-75 relative">
              <div class="w-full rounded-lg overflow-hidden">
                <video
                  v-if="slug === 'alchemy-family'"
                  class="w-full h-full object-contain"
                  autoplay
                  loop
                  muted
                  playsinline
                >
                  <source :src="pageData.heroImage?.url" type="video/mp4" />
                </video>
                <img
                  v-else-if="pageData.heroImage?.url"
                  :src="pageData.heroImage.url"
                  alt=""
                  class="w-full h-full object-cover"
                />
              </div>
              <NuxtLink
                v-if="slug === 'lampu-2024'"
                to="https://www.google.com/maps/d/edit?mid=1OOHIfMoJUrkf7suSf1GVfFXqp1A6xrs&ll=-8.629516990201635,115.09626343828693&z=19"
                class="absolute bottom-2 right-2 font-medium text-sm bg-[#151515] rounded-full py-0.5 px-3.5"
                target="_blank"
              >
                {{ t('see-on-google') }}
              </NuxtLink>

              <NuxtLink
                v-if="
                  slug === 'bangkok-tech-and-crypto-week-scavenger-hunt-2024'
                "
                to="https://www.google.com/maps/d/u/1/embed?mid=1URUuprLJjgBRS92o-VYZJ6oBWQ7FXGc&ehbc=2E312F&noprof=1"
                class="absolute bottom-3.5 right-3.5 font-medium text-sm bg-[#151515] rounded-full py-0.5 px-3.5"
                target="_blank"
              >
                {{ t('see-on-google') }}
              </NuxtLink>
            </div>
          </div>
        </div>
        <img
          src="https://belong.net/images/landing/lampu-2023/logo.webp"
          class="absolute bottom-0 -z-1 object-cover hidden lg:block"
        />
      </div>
    </section>

    <div class="flex flex-col gap-10">
      <template
        v-if="slug === 'bangkok-tech-and-crypto-week-scavenger-hunt-2024'"
      >
        <div
          class="carousel max-w-360 overflow-hidden flex mx-auto relative my-14 lg:mt-35 lg:mb-0"
        >
          <div
            v-for="(i, index) in 2"
            class="w-30 h-full absolute top-0 m-auto bg-gradient-to-r z-9"
            :class="{
              'left-0 from-[#151515] to-transparent': index === 0,
              'right-0 to-[#151515] from-transparent': index === 1,
            }"
          />
          <div
            v-for="i in 2"
            class="flex flex-row items-center gap-16.5 carousel-track pr-16.5"
          >
            <div
              v-for="item in partnerList"
              :key="item.id"
              :class="partnerSizeClasses[item.id]"
            >
              <NuxtLink :to="item.href">
                <div class="flex items-center justify-center">
                  <Icon
                    :name="item.src"
                    :class="item.class"
                    class="h-7.5 object-contain opacity-40"
                  />
                </div>
              </NuxtLink>
            </div>
          </div>
        </div>
      </template>
      <section class="relative">
        <div v-if="pageData.displayFuturedEvents && featuredEventData.length">
          <p class="text-[1.375rem] text-[#B5B5B5] text-center pb-5 lg:pt-22.5">
            {{ t('featured-event') }}
          </p>
          <div class="max-w-237.25 mx-auto">
            <template v-if="featuredEventData.length > 1">
              <div class="flex flex-col gap-3.5 mx-3.5">
                <template v-for="item in featuredEventData">
                  <NuxtLink :to="localeRoute(`/events/${item.id}`)">
                    <PostComponent
                      :image="`${appLink}/up/activities/${item.id}_${item.coverImage}.jpg`"
                    >
                      <template #dateTime>
                        <UiDateTime v-if="item.start" :value="item.start" />
                      </template>
                      <template #title>
                        {{ item.name }}
                      </template>
                      <template #description>
                        {{ item.description }}
                      </template>
                    </PostComponent>
                  </NuxtLink>
                </template>
              </div>
            </template>
            <template v-else>
              <template v-for="item in featuredEventData">
                <NuxtLink :to="localeRoute('/events/' + item.id)">
                  <PostComponent
                    :image="`${appLink}/up/activities/${item.id}_${item.coverImage}.jpg`"
                  >
                    <template #dateTime>
                      <UiDateTime v-if="item.start" :value="item.start" />
                    </template>
                    <template #title>
                      {{ item.name }}
                    </template>
                    <template #description>
                      {{ item.description }}
                    </template>
                  </PostComponent>
                </NuxtLink>
              </template>
            </template>
          </div>
        </div>
      </section>

      <div class="flex flex-col gap-10">
        <RelatedEvents :data="hubId" :class="{ 'order-1': isPlayapal }">
          <template #title>
            <p class="text-xl text-center text-[#B5B5B5] px-3.5 lg:px-0">
              <template v-if="isChainatlasOrTonx">
                {{ t('which-city-do-you-want-to-start-from') }}
              </template>
              <template v-else>
                {{ t('some-things-happenning') }}
                {{ pageData.title }}
              </template>
            </p>
          </template>
        </RelatedEvents>

        <div
          v-if="relatedHubs && pageData.displayRelatedHubs"
          class="flex flex-col gap-5"
        >
          <div class="flex flex-col items-center text-center px-3.5 lg:px-0">
            <p class="text-[#B5B5B5] text-xl">
              <template v-if="isPlayapal">
                {{ t('explore-camps', { n: pageData.title }) }}
              </template>
              <template v-else>
                {{ t('explore-hubs-related-to', { title: pageData.title }) }}
              </template>
            </p>
            <p class="text-[#ffb0f0] text-base">
              {{ t('happenings-updates-and-community-chats') }}
            </p>
          </div>
          <div v-if="relatedHubs.length > 10">
            <ClientOnly>
              <swiper-container
                class="explore-hubs-swiper"
                :space-between="14"
                :loop="true"
                :autoplay="{
                  delay: 2500,
                }"
                :breakpoints="swiperBreakpoints"
                :slidesOffsetBefore="14"
                :slidesOffsetAfter="14"
              >
                <swiper-slide v-for="hub in relatedHubs" :key="hub.id">
                  <NuxtLink
                    :to="localeRoute('/hubs/' + hub.hubname)"
                    class="no-underline"
                  >
                    <UiHubCard
                      :image="
                        hub.mediaData?.coverImage?.large
                          ? `${appLink}/${hub.mediaData?.coverImage?.large}`
                          : ''
                      "
                      :coverImageDominantColors="
                        hub.mediaData
                          ? Array.isArray(
                              hub.mediaData.coverImageDominantColors
                            )
                            ? hub.mediaData.coverImageDominantColors
                            : []
                          : []
                      "
                    >
                      <template #title> {{ hub.name }} </template>
                      <template v-if="hub.membersIDs.length > 100" #subheader>
                        {{ t('member', { count: hub.membersIDs.length }) }}
                      </template>
                      <template #avatar>
                        <UiAvatar
                          :url="
                            hub.mediaData?.coverImage?.large
                              ? `${appLink}/${hub.mediaData?.coverImage?.large}`
                              : ''
                          "
                        />
                      </template>
                      <template v-if="hub.hubType === 'nftCollection'" #badge>
                        <Icon
                          name="belong:hub-verified"
                          class="min-w-4.75 max-w-4.75 min-h-5 max-h-5 ml-2"
                        />
                      </template>
                    </UiHubCard>
                  </NuxtLink>
                </swiper-slide>
              </swiper-container>
            </ClientOnly>
          </div>
          <template v-else>
            <div class="flex flex-row flex-wrap gap-3.5 justify-center">
              <div
                v-for="(hub, index) in isChainatlasOrTonx
                  ? hubApiDataArray
                  : relatedHubs"
                :key="index"
                class="w-96.5"
              >
                <NuxtLink
                  :to="
                    localeRoute(
                      isChainatlasOrTonx
                        ? '/' + hub.slug
                        : '/hubs/' +
                            ('hubname' in hub && hub.hubname
                              ? hub.hubname
                              : hub.slug)
                    )
                  "
                  class="no-underline"
                >
                  <UiHubCard
                    :image="
                      hub.mediaData?.coverImage?.large
                        ? `${appLink}/${hub.mediaData?.coverImage?.large}`
                        : ''
                    "
                    :coverImageDominantColors="
                      hub.mediaData
                        ? Array.isArray(hub.mediaData.coverImageDominantColors)
                          ? hub.mediaData.coverImageDominantColors
                          : []
                        : []
                    "
                  >
                    <template #title>
                      {{
                        isChainatlasOrTonx
                          ? hub.title
                          : 'name' in hub && hub.name
                            ? hub.name
                            : hub.title
                      }}
                    </template>
                    <template v-if="hub.membersIDs.length > 100" #subheader>
                      {{ t('member', { count: hub.membersIDs.length }) }}
                    </template>
                    <template #avatar>
                      <UiAvatar
                        :url="
                          hub.mediaData?.coverImage?.large
                            ? `${appLink}/${hub.mediaData?.coverImage?.large}`
                            : ''
                        "
                      />
                    </template>
                    <template v-if="hub.hubType === 'nftCollection'" #badge>
                      <Icon
                        name="belong:hub-verified"
                        class="min-w-4.75 max-w-4.75 min-h-5 max-h-5 ml-2"
                      />
                    </template>
                  </UiHubCard>
                </NuxtLink>
              </div>
            </div>
          </template>
        </div>
      </div>

      <Bangkok
        v-if="slug === 'bangkok-tech-and-crypto-week-scavenger-hunt-2024'"
      />
    </div>

    <SiteFooter>
      <template #title>{{ pageData.title }}</template>
    </SiteFooter>
  </div>
</template>
